import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputBase from "@material-ui/core/InputBase";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import Modal from "../../../common/Modal";
import AccountProvider from "../AccountProvider";
import { copyToClipboard } from "../../../../utils/helperFunctions";

const useStyles = makeStyles((theme) => ({
    container: {
        marginTop: theme.spacing(3),
        minWidth: "350px",
        [theme.breakpoints.down("sm")]: {
            minWidth: "250px",
        },
    },
    input: {
        background: theme.palette.background.default,
        borderRadius: 4,
        border: "1px solid #fff",
        paddingLeft: theme.spacing(1),
        color: "#3D3D3D",
        width: "100%",
        height: 40,
        marginTop: theme.spacing(0.5),
    },
    placeholder: {
        fontSize: 14,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(1),
    },
    copyWrapper: {
        cursor: "pointer",
        margin: theme.spacing(1),
    },
    section: {
        marginTop: theme.spacing(3),
    },
    sectionTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: "#3D3D3D",
        marginBottom: theme.spacing(1),
    },
    sectionDesc: {
        fontSize: 13,
        color: "#6D6D6D",
        marginBottom: theme.spacing(1),
    },
    codeBlock: {
        background: theme.palette.background.default,
        borderRadius: 4,
        padding: theme.spacing(1.5),
        fontSize: 12,
        fontFamily: "monospace",
        color: "#3D3D3D",
        whiteSpace: "pre",
        overflowX: "auto",
        cursor: "pointer",
        position: "relative",
    },
    copyCode: {
        position: "absolute",
        top: 8,
        right: 8,
        cursor: "pointer",
        opacity: 0.5,
        "&:hover": {
            opacity: 1,
        },
    },
}));

const ChatId = (props: { open: boolean; setOpen: Function; chatId: string }) => {
    const classes = useStyles();

    const basicSnippet = `<script async src="https://cdn.kibo.ai/chat.js"></script>

<script>
  $kibo = {};
  $kibo.chatId = "${props.chatId}";
</script>`;

    const fullSnippet = `<script async src="https://cdn.kibo.ai/chat.js"></script>

<script>
  $kibo = {};
  $kibo.chatId = "${props.chatId}";

  // If the user is logged in
  $kibo.userId = "USER_ID";
  $kibo.name = "USER_NAME";
  $kibo.picture = "USER_PICTURE_URL";
  $kibo.email = "USER_EMAIL";
</script>`;

    return (
        <Modal open={props.open} onClose={() => props.setOpen(false)} hideActions={true} title={"Live Chat Setup"}>
            <AccountProvider provider="chat" />
            <div className={classes.container}>
                <span className={classes.placeholder}>Chat ID</span>
                <InputBase
                    id="outlined-basic"
                    disabled
                    className={classes.input}
                    value={props.chatId}
                    required={true}
                    onClick={() => copyToClipboard(props.chatId)}
                    endAdornment={
                        <div>
                            <FileCopyIcon fontSize="small" className={classes.copyWrapper} />
                        </div>
                    }
                />

                <div className={classes.section}>
                    <div className={classes.sectionTitle}>Step 1 — Add the chat widget to your website</div>
                    <div className={classes.sectionDesc}>
                        Paste this snippet before the closing <code>&lt;/body&gt;</code> tag on every page.
                    </div>
                    <div className={classes.codeBlock} onClick={() => copyToClipboard(basicSnippet)}>
                        {basicSnippet}
                        <FileCopyIcon fontSize="small" className={classes.copyCode} />
                    </div>
                </div>

                <div className={classes.section}>
                    <div className={classes.sectionTitle}>Step 2 — Identify logged-in users (optional)</div>
                    <div className={classes.sectionDesc}>
                        If your users are logged in, pass their details so conversations are linked to their account.
                    </div>
                    <div className={classes.codeBlock} onClick={() => copyToClipboard(fullSnippet)}>
                        {fullSnippet}
                        <FileCopyIcon fontSize="small" className={classes.copyCode} />
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ChatId;
